<template>
  <div class="decor-editor">
    <!-- 顶部栏 -->
    <div class="editor-topbar">
      <a-button icon="arrow-left" @click="back">返回</a-button>
      <a-tag :color="typeMeta[page.pageType] ? typeMeta[page.pageType].color : ''" style="margin-left: 12px;">
        {{ typeMeta[page.pageType] ? typeMeta[page.pageType].label : page.pageType }}
      </a-tag>
      <a-input placeholder="页面名称" v-model="name" style="width: 260px; margin-left: 8px;" />
      <span class="draft-tip">拖拽左侧组件到手机预览区排版；保存并发布后对顾客端生效</span>
      <a-button icon="save" :loading="saving" style="margin-left: auto;" @click="save">保存</a-button>
      <a-button type="primary" icon="cloud-upload" :loading="saving" style="margin-left: 8px;" @click="saveAndPublish">保存并发布</a-button>
    </div>
    <!-- 三栏主体 -->
    <div class="editor-body">
      <!-- 左栏：组件库（分组展示，支持拖入画布或点击添加） -->
      <div class="editor-left">
        <div class="panel-title">组件库</div>
        <div v-for="group in libGroups" :key="group.name" class="lib-group">
          <div class="lib-group-title">{{ group.name }}</div>
          <draggable
            :list="group.types"
            :options="{ group: { name: 'decor', pull: 'clone', put: false }, sort: false }"
            :clone="cloneMaterial">
            <div class="lib-item" v-for="t in group.types" :key="t.type" @click="addComponent(t.type)">
              <a-icon :type="t.icon" />
              <span>{{ t.label }}</span>
            </div>
          </draggable>
        </div>
      </div>
      <!-- 中栏：手机预览（拖拽排序 / 点选编辑 / 删除） -->
      <div class="editor-center">
        <div class="phone">
          <div v-if="components.length === 0" class="phone-empty">从左侧拖入组件，或点击组件添加</div>
          <draggable
            v-model="components"
            class="phone-canvas"
            :options="{ group: 'decor', animation: 150 }"
            @add="onCanvasAdd">
            <div
              v-for="(comp, index) in components"
              :key="index"
              class="comp-item"
              :class="{ 'comp-selected': selectedIndex === index }"
              @click="selectComponent(index)">
              <div class="comp-ops">
                <a-button size="small" icon="delete" title="删除" @click.stop="removeComponent(index)" />
              </div>
              <!-- banner -->
              <div v-if="comp.type === 'banner'" class="p-banner">
                <div class="p-banner-strip" v-if="comp.data.images && comp.data.images.length">
                  <div v-for="(img, idx) in comp.data.images" :key="idx" class="p-banner-cell">
                    <img v-if="resolveImg(img.url)" :src="resolveImg(img.url)" />
                    <div v-else class="p-placeholder">图片{{ idx + 1 }}</div>
                    <span class="p-badge">{{ idx + 1 }}</span>
                  </div>
                </div>
                <div v-else class="p-placeholder">轮播图</div>
              </div>
              <!-- search -->
              <div v-else-if="comp.type === 'search'" class="p-search">
                <span class="p-search-icon">🔍</span>
                <span class="p-search-text">{{ comp.data.placeholder || '搜索菜品' }}</span>
              </div>
              <!-- notice -->
              <div v-else-if="comp.type === 'notice'" class="p-notice">
                📢 {{ comp.data.text || '公告内容' }}
                <span v-if="comp.data.scrollable" class="p-notice-scroll">滚动</span>
              </div>
              <!-- grid-nav -->
              <div v-else-if="comp.type === 'grid-nav'" class="p-grid">
                <template v-if="comp.data.items && comp.data.items.length">
                  <div v-for="(item, idx) in comp.data.items" :key="idx" class="p-grid-item">
                    <img v-if="item.icon && resolveImg(item.icon)" class="p-grid-img" :src="resolveImg(item.icon)" />
                    <div v-else class="p-grid-icon">{{ (item.title || '导').charAt(0) }}</div>
                    <div class="p-grid-title">{{ item.title || '导航' }}</div>
                  </div>
                </template>
                <div v-else class="p-placeholder">金刚区</div>
              </div>
              <!-- title-bar -->
              <div v-else-if="comp.type === 'title-bar'" class="p-title-bar">
                <span class="p-tb-title">{{ comp.data.title || '标题' }}</span>
                <span class="p-tb-more" v-if="comp.data.moreText">{{ comp.data.moreText }} ›</span>
              </div>
              <!-- image-ads -->
              <div v-else-if="comp.type === 'image-ads'">
                <img v-if="comp.data.image && resolveImg(comp.data.image.url)" :src="resolveImg(comp.data.image.url)" class="p-ads-img" />
                <div v-else class="p-placeholder">单图广告</div>
              </div>
              <!-- magic-cube -->
              <div v-else-if="comp.type === 'magic-cube'" class="p-cube" :class="'cube-' + (comp.data.layout || '1x2')">
                <div v-for="(cell, idx) in (comp.data.items || [])" :key="idx" class="p-cube-cell">
                  <img v-if="resolveImg(cell.img)" :src="resolveImg(cell.img)" />
                  <span v-else>魔方{{ idx + 1 }}</span>
                </div>
              </div>
              <!-- button-group -->
              <div v-else-if="comp.type === 'button-group'" class="p-btn-group">
                <div v-for="(btn, idx) in (comp.data.items || [])" :key="idx" class="p-btn-item"
                     :style="{ width: (100 / (comp.data.cols || 4)) + '%' }">
                  <img v-if="btn.img && resolveImg(btn.img)" class="p-btn-img"
                       :class="{ 'p-round-img': comp.data.style === 'round' }" :src="resolveImg(btn.img)" />
                  <div v-else class="p-btn-txt" :class="{ 'p-round-txt': comp.data.style === 'round' }">{{ btn.title || '按钮' }}</div>
                  <div class="p-btn-title" v-if="btn.img && btn.title">{{ btn.title }}</div>
                </div>
              </div>
              <!-- rich-text -->
              <div v-else-if="comp.type === 'rich-text'" class="p-rich" v-html="comp.data.html || '富文本内容'"></div>
              <!-- goods-group -->
              <div v-else-if="comp.type === 'goods-group'" class="p-goods">
                <div class="p-goods-head">{{ comp.data.title || '商品组' }}</div>
                <div v-if="!comp.data.goods || !comp.data.goods.length" class="p-placeholder">商品组</div>
                <div v-for="gid in comp.data.goods" :key="gid" class="p-goods-card">
                  <img v-if="goodsImg(gid)" :src="goodsImg(gid)" class="p-goods-img" />
                  <div v-else class="p-goods-img p-goods-img-empty">图</div>
                  <div class="p-goods-info">
                    <div class="p-goods-name">{{ goodsName(gid) }}</div>
                    <div class="p-goods-price" v-if="goodsInfo(gid).minPrice !== undefined && goodsInfo(gid).minPrice !== null">¥{{ goodsInfo(gid).minPrice }}</div>
                  </div>
                </div>
              </div>
              <!-- float-btn -->
              <div v-else-if="comp.type === 'float-btn'" class="p-float">
                <div class="p-float-tag">悬浮按钮（顾客端固定右下角）</div>
                <div class="p-float-btns">
                  <div v-for="(f, idx) in (comp.data.items || [])" :key="idx" class="p-float-btn">
                    <img v-if="resolveImg(f.img)" :src="resolveImg(f.img)" />
                    <span v-else>+</span>
                  </div>
                </div>
              </div>
              <div v-else class="p-placeholder">未知组件 {{ comp.type }}</div>
            </div>
          </draggable>
        </div>
      </div>
      <!-- 右栏：属性表单 -->
      <div class="editor-right">
        <div class="panel-title">属性设置</div>
        <div v-if="!selected" class="right-empty">请在预览区选择组件</div>
        <a-form v-else :labelCol="{ span: 8 }" :wrapperCol="{ span: 16 }">
          <a-form-item label="组件类型">
            <a-tag>{{ typeLabel(selected.type) }}</a-tag>
          </a-form-item>
          <!-- banner -->
          <template v-if="selected.type === 'banner'">
            <a-form-item label="轮播图片">
              <div v-for="(img, idx) in (selected.data.images || [])" :key="idx" class="prop-img-row">
                <j-image-upload v-model="img.url" bizPath="material" text="上传" />
                <div class="prop-img-fields">
                  <a-input v-model="img.link" placeholder="跳转链接(可选)" />
                  <a-button size="small" icon="minus" @click="removeArrayItem(selected.data.images, idx)" />
                </div>
              </div>
              <a-button size="small" type="dashed" icon="plus" @click="addBannerImage">添加图片</a-button>
              <div class="prop-tip">建议尺寸 750×360px</div>
            </a-form-item>
            <a-form-item label="切换间隔(ms)">
              <a-input-number :min="500" :precision="0" v-model="selected.data.interval" style="width: 100%" />
            </a-form-item>
          </template>
          <!-- search -->
          <template v-else-if="selected.type === 'search'">
            <a-form-item label="占位文案">
              <a-input v-model="selected.data.placeholder" placeholder="如：搜索菜品" />
            </a-form-item>
            <div class="prop-tip">点击行为：已扫桌台进点餐页搜索，未扫码引导扫码点餐</div>
          </template>
          <!-- notice -->
          <template v-else-if="selected.type === 'notice'">
            <a-form-item label="公告内容">
              <a-textarea :rows="2" v-model="selected.data.text" />
            </a-form-item>
            <a-form-item label="滚动播放">
              <a-switch :checked="!!selected.data.scrollable" @click="() => selected.data.scrollable = !selected.data.scrollable" />
            </a-form-item>
          </template>
          <!-- grid-nav -->
          <template v-else-if="selected.type === 'grid-nav'">
            <a-form-item label="导航项">
              <div v-for="(item, idx) in (selected.data.items || [])" :key="idx" class="prop-img-row">
                <j-image-upload v-model="item.icon" bizPath="material" text="上传" />
                <div class="prop-img-fields">
                  <a-input v-model="item.title" placeholder="标题" style="width: 80px" />
                  <a-input v-model="item.link" placeholder="跳转链接" />
                  <a-button size="small" icon="minus" @click="removeArrayItem(selected.data.items, idx)" />
                </div>
              </div>
              <a-button size="small" type="dashed" icon="plus" @click="addGridItem">添加导航项</a-button>
              <div class="prop-tip">建议图标 200×200px；不传图标显示标题首字</div>
            </a-form-item>
          </template>
          <!-- title-bar -->
          <template v-else-if="selected.type === 'title-bar'">
            <a-form-item label="标题">
              <a-input v-model="selected.data.title" />
            </a-form-item>
            <a-form-item label="更多文字">
              <a-input v-model="selected.data.moreText" placeholder="留空则不显示" />
            </a-form-item>
            <a-form-item label="更多链接">
              <a-input v-model="selected.data.moreLink" placeholder="以/开头的小程序页面路径" />
            </a-form-item>
          </template>
          <!-- image-ads -->
          <template v-else-if="selected.type === 'image-ads'">
            <a-form-item label="广告图">
              <j-image-upload v-model="selected.data.image.url" bizPath="material" text="上传" />
              <div class="prop-tip">建议尺寸 750×400px</div>
            </a-form-item>
            <a-form-item label="跳转链接">
              <a-input v-model="selected.data.image.link" placeholder="以/开头的小程序页面路径" />
            </a-form-item>
          </template>
          <!-- magic-cube -->
          <template v-else-if="selected.type === 'magic-cube'">
            <a-form-item label="布局">
              <a-select v-model="selected.data.layout" @change="onCubeLayoutChange">
                <a-select-option value="1x2">一行两个</a-select-option>
                <a-select-option value="1x3">一行三个</a-select-option>
                <a-select-option value="2x2">两行四个</a-select-option>
              </a-select>
            </a-form-item>
            <a-form-item label="魔方图片">
              <div v-for="(cell, idx) in (selected.data.items || [])" :key="idx" class="prop-img-row">
                <j-image-upload v-model="cell.img" bizPath="material" text="上传" />
                <div class="prop-img-fields">
                  <a-input v-model="cell.link" placeholder="跳转链接(可选)" />
                </div>
              </div>
            </a-form-item>
          </template>
          <!-- button-group -->
          <template v-else-if="selected.type === 'button-group'">
            <a-form-item label="按钮形式">
              <a-radio-group v-model="selected.data.style">
                <a-radio value="square">方角</a-radio>
                <a-radio value="round">圆角</a-radio>
              </a-radio-group>
            </a-form-item>
            <a-form-item label="每行数量">
              <a-input-number :min="2" :max="5" :precision="0" v-model="selected.data.cols" style="width: 100%" />
            </a-form-item>
            <a-form-item label="按钮项">
              <div v-for="(item, idx) in (selected.data.items || [])" :key="idx" class="prop-img-row">
                <j-image-upload v-model="item.img" bizPath="material" text="上传" />
                <div class="prop-img-fields">
                  <a-input v-model="item.title" placeholder="文字" style="width: 70px" />
                  <a-input v-model="item.link" placeholder="跳转链接" />
                  <a-button size="small" icon="minus" @click="removeArrayItem(selected.data.items, idx)" />
                </div>
              </div>
              <a-button size="small" type="dashed" icon="plus" @click="addButtonItem">添加按钮</a-button>
              <div class="prop-tip">不传图片渲染为文字按钮；建议图标 200×200px</div>
            </a-form-item>
          </template>
          <!-- rich-text -->
          <template v-else-if="selected.type === 'rich-text'">
            <a-form-item label="内容">
              <a-textarea :rows="8" v-model="selected.data.html" placeholder="支持 HTML 标签，如 <p>活动规则</p><img src='图片地址'>" />
            </a-form-item>
          </template>
          <!-- goods-group -->
          <template v-else-if="selected.type === 'goods-group'">
            <a-form-item label="组标题">
              <a-input v-model="selected.data.title" />
            </a-form-item>
            <a-form-item label="已选商品">
              <div v-for="gid in (selected.data.goods || [])" :key="gid" class="prop-goods">
                <span>{{ goodsName(gid) }}</span>
                <a-button size="small" icon="close" @click="removeGoods(gid)" />
              </div>
              <a-button size="small" type="dashed" icon="plus" @click="openGoodsPicker">选择商品</a-button>
            </a-form-item>
          </template>
          <!-- float-btn -->
          <template v-else-if="selected.type === 'float-btn'">
            <a-form-item label="悬浮按钮">
              <div v-for="(f, idx) in (selected.data.items || [])" :key="idx" class="prop-img-row">
                <j-image-upload v-model="f.img" bizPath="material" text="上传" />
                <div class="prop-img-fields">
                  <a-input v-model="f.link" placeholder="跳转链接(可选)" />
                  <a-button size="small" icon="minus" @click="removeArrayItem(selected.data.items, idx)" />
                </div>
              </div>
              <a-button size="small" type="dashed" icon="plus" @click="addFloatItem"
                        :disabled="selected.data.items.length >= 4">添加按钮</a-button>
              <div class="prop-tip">最多 4 个，顾客端固定悬浮于页面右下角；建议 160×160px</div>
            </a-form-item>
          </template>
        </a-form>
      </div>
    </div>
    <!-- 商品选择弹窗 -->
    <a-modal
      title="选择商品"
      :visible="pickerVisible"
      :width="760"
      :footer="null"
      :maskClosable="false"
      @cancel="pickerVisible = false"
      style="top: 80px;">
      <a-spin :spinning="pickerLoading">
        <div style="margin-bottom: 12px;">
          <a-input-search
            placeholder="请输入商品名搜索"
            v-model="pickerSearch"
            style="width: 300px"
            allow-clear
            @search="() => loadGoods(1)" />
        </div>
        <a-table
          size="small"
          bordered
          rowKey="materialId"
          :columns="pickerColumns"
          :dataSource="pickerRows"
          :pagination="pickerPager"
          @change="onPickerChange">
          <template slot="checkSlot" slot-scope="text, record">
            <a-checkbox
              :checked="pickerTarget && pickerTarget.data.goods.indexOf(record.materialId) > -1"
              @click="() => togglePickGoods(record)" />
          </template>
        </a-table>
      </a-spin>
    </a-modal>
  </div>
</template>
<script>
import draggable from 'vuedraggable'
import JImageUpload from '@/components/jeecg/JImageUpload'
import { goodsMaterialsOptions, decorDetail, decorUpdate, decorPublish } from '@/api/plugin/dining'
import { getAction, getFileAccessHttpUrl } from '@/api/manage'

/**
 * 装修编辑器（拖拽式三栏）：左组件库（基础/营销/功能三组，拖入或点击添加）
 * / 中 375px 手机画布（拖拽排序、点选编辑、删除）/ 右属性面板（图片上传 + 链接配置）
 *
 * 组件 JSON 契约（与后端 DecorService 类注释一致，body/publish_body 存该数组字符串）：
 * [
 *   {"type":"banner","data":{"images":[{"url":"","link":""}],"interval":4000}},
 *   {"type":"search","data":{"placeholder":"搜索菜品"}},
 *   {"type":"notice","data":{"text":"公告内容","scrollable":true}},
 *   {"type":"grid-nav","data":{"items":[{"icon":"","title":"","link":""}]}},
 *   {"type":"title-bar","data":{"title":"标题","moreText":"更多","moreLink":""}},
 *   {"type":"image-ads","data":{"image":{"url":"","link":""}}},
 *   {"type":"magic-cube","data":{"layout":"1x2","items":[{"img":"","link":""}]}},
 *   {"type":"button-group","data":{"style":"square","cols":4,"items":[{"img":"","title":"","link":""}]}},
 *   {"type":"rich-text","data":{"html":""}},
 *   {"type":"goods-group","data":{"title":"推荐菜","goods":[1,2,3]}},
 *   {"type":"float-btn","data":{"items":[{"img":"","link":""}]}}
 * ]
 * goods-group 的 goods 为商品 id 数组（jsh_material.id）；图片字段存相对路径
 * （systemConfig/upload 返回值，如 material/63/xx.jpg）或完整 http 地址。
 *
 * 编辑数据来源：优先本地草稿 localStorage('decorDraft_'+pageId)；
 * 无草稿拉取管理端详情（body 编辑态优先，publishBody 快照兜底）。
 */
export default {
  name: 'DecorEditor',
  components: { draggable, JImageUpload },
  props: {
    page: { type: Object, required: true }
  },
  data() {
    return {
      name: '',
      components: [],
      selectedIndex: -1,
      saving: false,
      goodsMap: {},
      draftTimer: null,
      typeMeta: {
        MALL_HOME: { label: '商城首页', color: 'blue' },
        DINING_HOME: { label: '点餐首页', color: 'green' },
        CUSTOM: { label: '自定义', color: 'orange' }
      },
      libGroups: [
        { name: '基础组件', types: [
          { type: 'banner', label: '轮播图', icon: 'picture' },
          { type: 'search', label: '搜索框', icon: 'search' },
          { type: 'notice', label: '公告', icon: 'notification' },
          { type: 'title-bar', label: '标题栏', icon: 'align-left' },
          { type: 'image-ads', label: '单图广告', icon: 'file-image' },
          { type: 'magic-cube', label: '魔方', icon: 'block' },
          { type: 'rich-text', label: '富文本', icon: 'font-size' }
        ]},
        { name: '营销组件', types: [
          { type: 'grid-nav', label: '金刚区', icon: 'appstore' },
          { type: 'button-group', label: '按钮组', icon: 'gift' },
          { type: 'goods-group', label: '商品组', icon: 'shopping' }
        ]},
        { name: '功能组件', types: [
          { type: 'float-btn', label: '悬浮按钮', icon: 'customer-service' }
        ]}
      ],
      pickerVisible: false,
      pickerLoading: false,
      pickerSearch: '',
      pickerTarget: null,
      pickerRows: [],
      pickerPager: { current: 1, pageSize: 10, total: 0, showTotal: (total) => `共 ${total} 条` },
      pickerColumns: [
        { title: '选择', dataIndex: 'check', width: 60, align: 'center', scopedSlots: { customRender: 'checkSlot' } },
        { title: '商品名', dataIndex: 'materialName', align: 'left' },
        { title: '最低价', dataIndex: 'minPrice', width: 100, align: 'right',
          customRender: function(text) { return text === null || text === undefined || text === '' ? '-' : text } }
      ]
    }
  },
  computed: {
    selected() {
      return this.selectedIndex > -1 ? this.components[this.selectedIndex] : null
    },
    draftKey() {
      return 'decorDraft_' + this.page.id
    }
  },
  watch: {
    components: {
      handler() {
        this.scheduleDraft()
      },
      deep: true
    },
    name() {
      this.scheduleDraft()
    }
  },
  created() {
    this.init()
  },
  beforeDestroy() {
    if (this.draftTimer) {
      clearTimeout(this.draftTimer)
      this.draftTimer = null
    }
  },
  methods: {
    init() {
      this.name = this.page.name || ''
      let draft = null
      try {
        draft = JSON.parse(localStorage.getItem(this.draftKey) || 'null')
      } catch (e) {
        draft = null
      }
      if (draft && Array.isArray(draft.components)) {
        this.components = this.normalizeComponents(draft.components)
        if (draft.name) {
          this.name = draft.name
        }
        this.$message.info('已恢复本地未保存的草稿')
      } else {
        // 无草稿：拉取服务端 body 编辑态（无则回退 publishBody 发布快照），均无则空白开始
        this.loadServerBody()
      }
    },
    /** 草稿/快照数据归一化：缺失的契约字段补默认值，防畸形 JSON 渲染报错 */
    normalizeComponents(list) {
      return list.map((comp) => {
        const type = comp && comp.type
        return { type: type, data: Object.assign(this.defaultData(type), (comp && comp.data) || {}) }
      })
    },
    /** 加载服务端数据：管理端详情接口（body 编辑态优先，publishBody 快照兜底） */
    loadServerBody() {
      decorDetail(this.page.id).then((res) => {
        if (res.code === 200 && res.data) {
          let json = res.data.body
          if (!json && res.data.publishBody) {
            json = res.data.publishBody
          }
          if (json) {
            try {
              const list = JSON.parse(json)
              if (Array.isArray(list) && list.length > 0) {
                this.components = this.normalizeComponents(list)
                this.resolveGoodsNames()
              }
            } catch (e) {
              // 服务端数据异常时从空白开始，不阻塞编辑
            }
          }
        }
      })
    },
    /** 用组件商品引用回填商品名/图/价（goodsMap 供预览显示） */
    resolveGoodsNames() {
      const ids = []
      this.components.forEach((comp) => {
        if (comp.type === 'goods-group' && Array.isArray(comp.data.goods)) {
          comp.data.goods.forEach((id) => {
            if (ids.indexOf(id) === -1) {
              ids.push(id)
            }
          })
        }
      })
      if (!ids.length) {
        return
      }
      getAction('/api/plugin/dining/decor/mini/goods-group', { refIds: ids.join(',') }).then((res) => {
        if (res.code === 200 && res.data && Array.isArray(res.data.rows)) {
          res.data.rows.forEach((r) => {
            this.$set(this.goodsMap, r.id, r)
          })
        }
      })
    },
    typeLabel(type) {
      for (let g = 0; g < this.libGroups.length; g++) {
        const meta = this.libGroups[g].types.find((t) => t.type === type)
        if (meta) {
          return meta.label
        }
      }
      return type
    },
    /** 各组件新增时的默认数据（契约字段全集） */
    defaultData(type) {
      switch (type) {
        case 'banner':
          return { images: [{ url: '', link: '' }], interval: 4000 }
        case 'search':
          return { placeholder: '搜索菜品' }
        case 'notice':
          return { text: '公告内容', scrollable: true }
        case 'grid-nav':
          return { items: [{ icon: '', title: '导航', link: '' }] }
        case 'title-bar':
          return { title: '标题', moreText: '更多', moreLink: '' }
        case 'image-ads':
          return { image: { url: '', link: '' } }
        case 'magic-cube':
          return { layout: '1x2', items: [{ img: '', link: '' }, { img: '', link: '' }] }
        case 'button-group':
          return { style: 'square', cols: 4, items: [{ img: '', title: '按钮', link: '' }] }
        case 'rich-text':
          return { html: '' }
        case 'goods-group':
          return { title: '商品组', goods: [] }
        case 'float-btn':
          return { items: [{ img: '', link: '' }] }
        default:
          return {}
      }
    },
    /** 组件库拖入画布时克隆出新组件（不复用库内配置对象） */
    cloneMaterial(libItem) {
      return { type: libItem.type, data: this.defaultData(libItem.type) }
    },
    /** 画布新增（从组件库拖入）后自动选中 */
    onCanvasAdd(evt) {
      this.selectedIndex = evt.newIndex
    },
    addComponent(type) {
      this.components.push({ type: type, data: this.defaultData(type) })
      this.selectedIndex = this.components.length - 1
    },
    selectComponent(index) {
      this.selectedIndex = index
    },
    removeComponent(index) {
      this.components.splice(index, 1)
      if (this.selectedIndex === index) {
        this.selectedIndex = -1
      } else if (this.selectedIndex > index) {
        this.selectedIndex--
      }
    },
    removeArrayItem(arr, idx) {
      if (arr) {
        arr.splice(idx, 1)
      }
    },
    addBannerImage() {
      this.selected.data.images.push({ url: '', link: '' })
    },
    addGridItem() {
      this.selected.data.items.push({ icon: '', title: '', link: '' })
    },
    addButtonItem() {
      this.selected.data.items.push({ img: '', title: '', link: '' })
    },
    addFloatItem() {
      this.selected.data.items.push({ img: '', link: '' })
    },
    /** 魔方布局切换：按布局格数补齐/裁剪 items（1x2→2 格，1x3→3 格，2x2→4 格） */
    onCubeLayoutChange(val) {
      const need = val === '1x3' ? 3 : (val === '2x2' ? 4 : 2)
      const items = this.selected.data.items
      while (items.length < need) {
        items.push({ img: '', link: '' })
      }
      if (items.length > need) {
        items.splice(need)
      }
    },
    /** 图片地址：http 开头原样返回（外链），否则拼 systemConfig/static 相对路径 */
    resolveImg(url) {
      if (!url) {
        return ''
      }
      if (url.indexOf('http') === 0) {
        return url
      }
      return getFileAccessHttpUrl('systemConfig/static/' + url)
    },
    goodsInfo(id) {
      return this.goodsMap[id] || {}
    },
    goodsName(id) {
      return this.goodsInfo(id).name || '商品#' + id
    },
    goodsImg(id) {
      const imgName = this.goodsInfo(id).imgName
      if (imgName && imgName.split(',')) {
        return getFileAccessHttpUrl('systemConfig/static/' + imgName.split(',')[0])
      }
      return ''
    },
    // ===== 商品选择弹窗 =====
    openGoodsPicker() {
      this.pickerSearch = ''
      this.pickerTarget = this.selected
      this.pickerVisible = true
      this.loadGoods(1)
    },
    loadGoods(currentPage) {
      if (currentPage) {
        this.pickerPager.current = currentPage
      }
      this.pickerLoading = true
      goodsMaterialsOptions({
        search: (this.pickerSearch || '').trim(),
        currentPage: this.pickerPager.current,
        pageSize: this.pickerPager.pageSize
      }).then((res) => {
        if (res.code === 200) {
          this.pickerRows = res.data.rows || []
          this.pickerPager.total = res.data.total || 0
        } else {
          this.$message.warning(res.message)
        }
      }).finally(() => {
        this.pickerLoading = false
      })
    },
    onPickerChange(pagination) {
      this.pickerPager.current = pagination.current
      this.loadGoods()
    },
    togglePickGoods(record) {
      const goods = this.pickerTarget.data.goods
      const idx = goods.indexOf(record.materialId)
      if (idx > -1) {
        goods.splice(idx, 1)
      } else {
        goods.push(record.materialId)
        this.$set(this.goodsMap, record.materialId, {
          id: record.materialId,
          name: record.materialName,
          imgName: record.imgName,
          minPrice: record.minPrice
        })
      }
    },
    removeGoods(gid) {
      const idx = this.selected.data.goods.indexOf(gid)
      if (idx > -1) {
        this.selected.data.goods.splice(idx, 1)
      }
    },
    // ===== 草稿与保存 =====
    scheduleDraft() {
      if (this.draftTimer) {
        clearTimeout(this.draftTimer)
      }
      this.draftTimer = setTimeout(() => {
        this.draftTimer = null
        this.saveDraft()
      }, 300)
    },
    saveDraft() {
      try {
        localStorage.setItem(this.draftKey, JSON.stringify({ name: this.name, components: this.components }))
      } catch (e) {
        // 存储失败（隐私模式/满）忽略，不影响编辑
      }
    },
    clearDraft() {
      try {
        localStorage.removeItem(this.draftKey)
      } catch (e) {
        // 忽略
      }
    },
    save() {
      this.doSave(false)
    },
    saveAndPublish() {
      this.doSave(true)
    },
    /** 保存编辑态；publishAfter=true 时保存成功后接着发布（同类型唯一已发布页会被替换） */
    doSave(publishAfter) {
      if (!this.name || !this.name.trim()) {
        this.$message.warning('请输入页面名称')
        return
      }
      this.saving = true
      decorUpdate(this.page.id, {
        name: this.name.trim(),
        body: JSON.stringify(this.components)
      }).then((res) => {
        if (res.code !== 200) {
          this.$message.warning(res.message)
          return
        }
        this.clearDraft()
        if (!publishAfter) {
          this.$message.success('保存成功，发布后对顾客端生效')
          return
        }
        return decorPublish(this.page.id).then((r) => {
          if (r.code === 200) {
            this.$message.success('已保存并发布')
          } else {
            this.$message.warning(r.message)
          }
        })
      }).finally(() => {
        this.saving = false
      })
    },
    back() {
      this.saveDraft()
      this.$emit('back')
    }
  }
}
</script>
<style scoped>
.decor-editor { background: #fff; min-height: 100%; }
.editor-topbar { display: flex; align-items: center; padding: 12px 16px; border-bottom: 1px solid #e8e8e8; }
.draft-tip { color: #999; font-size: 12px; margin-left: 16px; }
.editor-body { display: flex; align-items: flex-start; padding: 16px; }
.panel-title { font-weight: bold; margin-bottom: 12px; }
/* 左栏 */
.editor-left { width: 250px; flex-shrink: 0; }
.lib-group { margin-bottom: 14px; }
.lib-group-title { font-size: 12px; color: #999; margin-bottom: 8px; }
.lib-item { display: flex; align-items: center; width: 200px; padding: 10px 16px; border: 1px solid #e8e8e8; border-radius: 4px; margin-bottom: 8px; cursor: grab; user-select: none; }
.lib-item:hover { border-color: #1890ff; color: #1890ff; }
.lib-item .anticon { margin-right: 8px; }
/* 中栏 */
.editor-center { flex: 1; display: flex; justify-content: center; }
.phone { position: relative; width: 375px; height: 640px; border: 10px solid #2b2b2b; border-radius: 24px; overflow-y: auto; background: #f5f5f5; box-sizing: content-box; }
.phone-canvas { min-height: 600px; }
.phone-empty { position: absolute; top: 40%; left: 0; right: 0; text-align: center; color: #bbb; font-size: 13px; pointer-events: none; }
.comp-item { position: relative; margin: 6px; border: 1px dashed transparent; cursor: pointer; }
.comp-item:hover { border-color: #91d5ff; }
.comp-selected { border: 2px solid #1890ff !important; }
.comp-ops { position: absolute; top: 2px; right: 2px; z-index: 10; display: none; }
.comp-item:hover .comp-ops, .comp-selected .comp-ops { display: inline-block; }
.comp-ops .ant-btn { margin-left: 2px; padding: 0 4px; }
.phone img { pointer-events: none; } /* 防止画布内图片原生拖拽干扰排序 */
/* 预览组件样式 */
.p-placeholder { height: 80px; line-height: 80px; text-align: center; color: #bbb; background: #ececec; font-size: 13px; }
.p-banner-strip { display: flex; overflow-x: auto; }
.p-banner-cell { position: relative; flex-shrink: 0; }
.p-banner-cell img { width: 200px; height: 90px; object-fit: cover; display: block; }
.p-banner-cell .p-placeholder { width: 200px; }
.p-badge { position: absolute; left: 4px; top: 4px; background: rgba(0,0,0,.5); color: #fff; font-size: 11px; padding: 0 5px; border-radius: 8px; }
.p-search { display: flex; align-items: center; background: #fff; border-radius: 16px; padding: 8px 12px; color: #999; font-size: 13px; }
.p-search-icon { margin-right: 6px; }
.p-notice { display: flex; align-items: center; background: #fff7e6; border: 1px solid #ffd591; color: #d46b08; padding: 6px 10px; font-size: 13px; }
.p-notice-scroll { margin-left: auto; font-size: 11px; color: #ffa940; }
.p-grid { display: flex; flex-wrap: wrap; background: #fff; padding: 10px 6px; }
.p-grid-item { width: 25%; text-align: center; margin-bottom: 8px; }
.p-grid-img { width: 40px; height: 40px; object-fit: cover; border-radius: 8px; }
.p-grid-icon { font-size: 20px; height: 40px; line-height: 40px; width: 40px; margin: 0 auto; background: #f0f8ff; color: #1890ff; border-radius: 8px; }
.p-grid-title { font-size: 12px; color: #333; margin-top: 4px; }
.p-title-bar { display: flex; align-items: center; background: #fff; padding: 10px 12px; }
.p-tb-title { font-weight: bold; font-size: 15px; }
.p-tb-more { margin-left: auto; font-size: 12px; color: #999; }
.p-ads-img { width: 100%; display: block; }
.p-cube { display: flex; flex-wrap: wrap; }
.p-cube-cell { box-sizing: border-box; padding: 2px; height: 95px; display: flex; align-items: center; justify-content: center; background: #ececec; color: #bbb; font-size: 12px; }
.p-cube.cube-1x2 .p-cube-cell { width: 50%; }
.p-cube.cube-1x3 .p-cube-cell { width: 33.33%; height: 63px; }
.p-cube.cube-2x2 .p-cube-cell { width: 50%; height: 93px; }
.p-cube-cell img { width: 100%; height: 100%; object-fit: cover; display: block; }
.p-btn-group { display: flex; flex-wrap: wrap; background: #fff; padding: 8px 4px; }
.p-btn-item { display: flex; flex-direction: column; align-items: center; padding: 6px 4px; box-sizing: border-box; }
.p-btn-img { width: 44px; height: 44px; object-fit: cover; background: #ececec; }
.p-btn-txt { width: 90%; height: 32px; background: #0081ff; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 12px; margin: 4px 0; }
.p-round-img { border-radius: 50%; }
.p-round-txt { border-radius: 16px; }
.p-btn-title { font-size: 11px; color: #555; margin-top: 4px; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.p-rich { background: #fff; padding: 8px 10px; min-height: 40px; font-size: 13px; }
.p-goods { background: #fff; padding: 10px 12px; }
.p-goods-head { font-weight: bold; font-size: 15px; margin-bottom: 8px; }
.p-goods-card { display: flex; align-items: center; margin-bottom: 8px; }
.p-goods-img { width: 60px; height: 60px; object-fit: cover; border-radius: 4px; }
.p-goods-img-empty { background: #ececec; color: #bbb; text-align: center; line-height: 60px; }
.p-goods-info { margin-left: 10px; flex: 1; min-width: 0; }
.p-goods-name { font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.p-goods-price { color: #f5222d; font-size: 13px; margin-top: 4px; }
.p-float { position: relative; border: 1px dashed #b3d8ff; background: #f0f8ff; padding: 8px; }
.p-float-tag { font-size: 11px; color: #1890ff; margin-bottom: 6px; }
.p-float-btns { display: flex; }
.p-float-btn { width: 40px; height: 40px; border-radius: 50%; background: #fff; border: 1px solid #d9d9d9; margin-right: 8px; display: flex; align-items: center; justify-content: center; color: #bbb; overflow: hidden; }
.p-float-btn img { width: 100%; height: 100%; object-fit: cover; }
/* 右栏 */
.editor-right { width: 340px; flex-shrink: 0; border-left: 1px solid #e8e8e8; margin-left: 16px; padding-left: 16px; min-height: 500px; }
.right-empty { color: #999; padding: 24px 0; }
.prop-img-row { display: flex; align-items: flex-start; margin-bottom: 8px; }
.prop-img-fields { flex: 1; min-width: 0; margin-left: 8px; display: flex; flex-wrap: wrap; gap: 4px; }
.prop-img-fields .ant-input { flex: 1; min-width: 80px; }
.prop-tip { color: #999; font-size: 12px; margin-top: 4px; line-height: 1.5; }
.prop-goods { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; }
</style>
